/**
 * SignUpPrompt — Phase 1 / Wave 3 / W3.T5
 *
 * Modal that surfaces sign-up CTAs (Google, Apple on iOS, email magic-link) to
 * anonymous users at intent-based moments (Upgrade, Save-across-devices).
 * Dismissible per AUTH-08. Sign in with Apple compare solo su iOS (#31).
 *
 * 2026-07 (email sign-in widen): the email magic-link send sub-view was
 * RESTORED here (it had been removed in W4 patch 2 for the v1.1 Google-only
 * ship) so non-Google users (Outlook/iCloud/any email) can sign in from this
 * nudge too. Mirrors the SignInModal send UI: email input + "Email me a
 * sign-in link" → `sendMagicLink` → "Check your inbox" state.
 *
 * Design follows PATTERNS §5: same overlay shell as `HowItWorksModal`
 * (rgba(0,0,0,0.5) backdrop + blur(8px)), candy_heart card, 2rem radius.
 *
 * Props:
 *   - open (bool): controls visibility
 *   - onClose (fn): close handler (backdrop click, ✕, "Not now")
 *   - onSuccess (fn): called after a successful sign-in (Google or Apple)
 *   - dark (bool): tema scuro, per il pulsante Apple bianco
 *   - microcopy (object): theme-scoped copy with keys signUpPromptTitle,
 *     signUpPromptBody, signUpCtaGoogle, signUpSkip
 *     (signUpCtaEmail is preserved in theme microcopy for Phase 3
 *     re-enable but no longer rendered.)
 *   - trigger (string, optional): "upgrade" | "save-across-devices" | "reactions".
 *     After a publish and from the share screen ("save-across-devices",
 *     "reactions") the title and body talk about the video reactions (#110):
 *     an anonymous creator never receives them.
 */
import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  signInWithGoogle,
  signInWithApple,
  appleSignInAvailable,
  sendMagicLink,
  friendlyAuthError,
} from "../services/authService";
import AppleSignInButton from "./AppleSignInButton";

const FALLBACK = {
  signUpPromptTitle: "Save your puzzles across devices",
  signUpPromptBody: "Sign in once — your puzzles follow you anywhere.",
  signUpCtaGoogle: "Continue with Google",
  signUpSkip: "Not now",
};

const REACTION_TRIGGERS = ["save-across-devices", "reactions"];

export default function SignUpPrompt({
  open,
  onClose,
  onSuccess,
  microcopy = {},
  trigger = "unknown",
  dark = false,
}) {
  const { t } = useTranslation();
  const copy = { ...FALLBACK, ...microcopy };
  if (REACTION_TRIGGERS.includes(trigger)) {
    copy.signUpPromptTitle = t("ui.signUp.reactionsTitle");
    copy.signUpPromptBody = t("ui.signUp.reactionsBody");
  }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [linkState, setLinkState] = useState("idle"); // idle | sending | sent

  if (!open) return null;

  const handleSignIn = (signIn) => async () => {
    setBusy(true);
    setError("");
    try {
      await signIn();
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSendLink = async () => {
    if (!email || !/.+@.+\..+/.test(email)) {
      setError(t("ui.signUp.invalidEmail"));
      return;
    }
    setLinkState("sending");
    setError("");
    try {
      await sendMagicLink(email.trim());
      setLinkState("sent");
    } catch (err) {
      setError(friendlyAuthError(err));
      setLinkState("idle");
    }
  };

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)",
    zIndex: 320,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  };
  const cardStyle = {
    background: "var(--color-surface)",
    borderRadius: "2rem",
    padding: 32,
    maxWidth: 420,
    width: "100%",
    boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
    position: "relative",
  };
  const closeBtn = {
    position: "absolute",
    top: 16,
    right: 16,
    background: "var(--color-bgAlt)",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "var(--color-textMuted)",
    fontSize: 16,
    fontWeight: 700,
  };
  const ctaPrimary = {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: 16,
    fontFamily: "var(--font-body)",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    background:
      "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
    color: "var(--color-onPrimary)",
    boxShadow: "0 8px 24px var(--color-glow)",
  };
  const ctaSecondary = {
    ...ctaPrimary,
    background: "var(--color-bgAlt)",
    color: "var(--color-text)",
    boxShadow: "none",
  };
  const skipStyle = {
    display: "block",
    margin: "20px auto 0",
    background: "none",
    border: "none",
    color: "var(--color-textMuted)",
    fontFamily: "var(--font-body)",
    fontSize: 14,
    cursor: "pointer",
    textDecoration: "underline",
  };

  return (
    <div onClick={onClose} style={overlayStyle} data-trigger={trigger}>
      <div onClick={(e) => e.stopPropagation()} style={cardStyle}>
        <button onClick={onClose} style={closeBtn} aria-label={t("ui.signUp.closeAria")}>
          ✕
        </button>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 20,
            margin: "0 auto 16px",
            background:
              "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            boxShadow: "0 8px 24px var(--color-glow)",
          }}
        >
          🔐
        </div>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 22,
            fontWeight: 800,
            color: "var(--color-text)",
            margin: "0 0 8px",
            textAlign: "center",
          }}
        >
          {copy.signUpPromptTitle}
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 14,
            color: "var(--color-textMuted)",
            textAlign: "center",
            margin: "0 0 24px",
            lineHeight: 1.5,
          }}
        >
          {copy.signUpPromptBody}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button onClick={handleSignIn(signInWithGoogle)} disabled={busy} style={ctaPrimary}>
            {copy.signUpCtaGoogle}
          </button>
          {appleSignInAvailable() && (
            <AppleSignInButton onClick={handleSignIn(signInWithApple)} busy={busy} dark={dark} />
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0 12px", color: "var(--color-textMuted)", fontFamily: "var(--font-body)", fontSize: 13 }}>
          <span style={{ flex: 1, height: 2, background: "var(--color-bgAlt)", borderRadius: 2 }} /> {t("ui.signUp.or")} <span style={{ flex: 1, height: 2, background: "var(--color-bgAlt)", borderRadius: 2 }} />
        </div>

        {linkState === "sent" ? (
          <div style={{ textAlign: "center" }}>
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, color: "var(--color-text)", margin: "0 0 4px" }}>
              {t("ui.signUp.inboxTitle")}
            </p>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-textMuted)", margin: 0 }}>
              <Trans i18nKey="ui.signUp.inboxBody" values={{ email }} components={{ strong: <strong /> }} />
            </p>
          </div>
        ) : (
          <>
            <input
              type="email"
              aria-label={t("ui.signUp.emailAria")}
              placeholder={t("ui.signUp.emailPlaceholder")}
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && linkState !== "sending") handleSendLink(); }}
              disabled={linkState === "sending"}
              style={{ width: "100%", padding: "14px 16px", border: "none", borderRadius: 12, background: "var(--color-bgAlt)", fontFamily: "var(--font-body)", fontSize: 16, color: "var(--color-text)", boxSizing: "border-box", marginBottom: 10 }}
            />
            <button type="button" onClick={handleSendLink} disabled={linkState === "sending"} style={ctaSecondary}>
              {linkState === "sending" ? t("ui.signUp.sending") : t("ui.signUp.sendLink")}
            </button>
          </>
        )}

        {error && (
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 13,
              color: "var(--color-wrong)",
              textAlign: "center",
              margin: "12px 0 0",
            }}
            role="alert"
          >
            {error}
          </p>
        )}

        <button onClick={onClose} style={skipStyle}>
          {copy.signUpSkip}
        </button>
      </div>
    </div>
  );
}
